/**
 * Чистые правила формы IT-работ (ТЗ-2026-АКТ001): состояние формы, сборка тела запроса,
 * разбор payload черновика/доработки и зеркало серверных правил подачи.
 *
 * `itWorksErrors` повторяет validateItWorks бэка (as-life-api `src/edo/it-works/it-works.helpers.ts`)
 * в том же порядке, с теми же полями и текстами — всё, что проверяемо без сервера. Не повторяются:
 * MIME загруженного файла (формат проверяется при выборе), привязка автора к учётке (в опциях её
 * нет), неактивные люди, повтор id (id выдаёт сама форма). Источник правды — сервер.
 */
import type { Lang, Localized } from '../../../i18n'
import { MONTHS } from '../../../ui/DateField'
import type {
  EdoAttachment, ItWorksCurrency, ItWorksDirection, ItWorksInput, ItWorksOptions, ItWorksPerson,
  ItWorksReportKind,
} from '../../../lib/types'

export const IT_WORKS_CURRENCIES: ItWorksCurrency[] = ['GEL', 'AZN', 'USD']
/** Лимит файлов заявки: отчёты, приложения и счёт вместе (MAX_IT_WORKS_FILES бэка). */
export const IT_WORKS_MAX_FILES = 20
/** Лимит загрузки uploads (25 МБ, тот же у nginx). */
export const IT_WORKS_MAX_FILE_BYTES = 25 * 1024 * 1024
const MAX_COMMENT = 2000

/** Файл формы. Нет `name` — файл из черновика: payload хранит только uploadId. `fresh` — загружен в этой правке. */
export interface ItWorksFile { uploadId: string; name?: string; fresh?: boolean }
export interface ItWorksReportRow { id: string; kind: ItWorksReportKind; file: ItWorksFile | null; authorId: string; note: string }
export interface ItWorksLineRow {
  id: string
  /** undefined — модуль ещё не выбран, null — «Другое» (тогда `otherName`). */
  moduleId?: string | null
  otherName: string
  /** Только Баку: id отчёта строки. */
  reportId: string
  approver: ItWorksPerson | null
  note: string
}
export interface ItWorksFormState {
  direction: ItWorksDirection
  contractorId: string
  /** `YYYY-MM`. */
  period: string
  periodFrom: string
  periodTo: string
  /** Как введено: строка, чтобы не терять «12.» при наборе. */
  amount: string
  currency: ItWorksCurrency | ''
  invoice: ItWorksFile | null
  finalApproverId: string
  recipients: ItWorksPerson[]
  comment: string
  /** Порядок = порядок в теле запроса: по нему сервер адресует `reports[i]`. */
  reports: ItWorksReportRow[]
  modules: ItWorksLineRow[]
}

const dupModule = (name: string): Localized => ({
  ru: `Модуль «${name}» уже есть в заявке`,
  en: `Module «${name}» is already in the request`,
  az: `«${name}» modulu artıq müraciətdədir`,
})

/** Тексты сервера (ru — точно как в it-works.helpers.ts), en/az — переводы (az — черновые). */
export const MSG = {
  contractor: { ru: 'Выберите подрядчика', en: 'Select a contractor', az: 'Podratçını seçin' },
  period: { ru: 'Укажите отчётный период', en: 'Specify the reporting period', az: 'Hesabat dövrünü göstərin' },
  periodTo: { ru: 'Дата «по» не может быть раньше даты «с»', en: 'The “to” date cannot be earlier than the “from” date', az: '«Bitmə» tarixi «başlanğıc» tarixindən əvvəl ola bilməz' },
  amount: { ru: 'Укажите сумму к оплате', en: 'Specify the amount to pay', az: 'Ödəniləcək məbləği göstərin' },
  currency: { ru: 'Выберите валюту', en: 'Select a currency', az: 'Valyutanı seçin' },
  reportsGeorgia: { ru: 'Приложите сводный отчёт — один файл', en: 'Attach the summary report — one file', az: 'Yekun hesabatı əlavə edin — bir fayl' },
  reportsBaku: { ru: 'Добавьте хотя бы один отчёт', en: 'Add at least one report', az: 'Ən azı bir hesabat əlavə edin' },
  reportKind: { ru: 'Неверный тип отчёта для направления', en: 'Wrong report type for the direction', az: 'İstiqamət üçün hesabat növü yanlışdır' },
  reportAuthor: { ru: 'Выберите автора отчёта', en: 'Select the report author', az: 'Hesabatın müəllifini seçin' },
  reportFormat: { ru: 'Отчёты — только PDF, DOCX или XLSX', en: 'Reports — PDF, DOCX or XLSX only', az: 'Hesabatlar — yalnız PDF, DOCX və ya XLSX' },
  // Только клиент: строка отчёта без файла (сервер на это отвечает reportFormat — тот текст читался бы как «не тот формат»).
  reportFile: { ru: 'Прикрепите файл отчёта', en: 'Attach the report file', az: 'Hesabat faylını əlavə edin' },
  invoice: { ru: 'Счёт подрядчика — только PDF', en: 'Contractor invoice — PDF only', az: 'Podratçının hesabı — yalnız PDF' },
  files: { ru: `Не больше ${IT_WORKS_MAX_FILES} файлов в заявке`, en: `No more than ${IT_WORKS_MAX_FILES} files per request`, az: `Müraciətdə ${IT_WORKS_MAX_FILES} fayldan çox olmamalıdır` },
  dupFile: { ru: 'Один файл приложен дважды', en: 'The same file is attached twice', az: 'Eyni fayl iki dəfə əlavə edilib' },
  noModules: { ru: 'Добавьте хотя бы один модуль', en: 'Add at least one module', az: 'Ən azı bir modul əlavə edin' },
  pickModule: { ru: 'Выберите модуль', en: 'Select a module', az: 'Modulu seçin' },
  otherName: { ru: 'Укажите название модуля', en: 'Specify the module name', az: 'Modulun adını göstərin' },
  lineReport: { ru: 'Выберите отчёт для строки модуля', en: 'Select the report for the module line', az: 'Modul sətri üçün hesabatı seçin' },
  noApprover: { ru: 'Укажите согласующего', en: 'Specify the approver', az: 'Razılaşdıranı göstərin' },
  selfApprover: { ru: 'Нельзя назначить согласующим самого себя', en: 'You cannot assign yourself as the approver', az: 'Özünüzü razılaşdıran təyin edə bilməzsiniz' },
  authorApproves: { ru: 'Автор отчёта не может согласовывать свою работу', en: 'A report author cannot approve their own work', az: 'Hesabatın müəllifi öz işini razılaşdıra bilməz' },
  finalList: { ru: 'Выберите финального утверждающего из списка направления', en: 'Select the final approver from the direction list', az: 'Yekun təsdiqləyicini istiqamətin siyahısından seçin' },
  finalIsLineApprover: { ru: 'Финальный утверждающий не может согласовывать модуль', en: 'The final approver cannot approve a module', az: 'Yekun təsdiqləyici modulu razılaşdıra bilməz' },
  authorFinal: { ru: 'Автор отчёта не может утверждать свою работу', en: 'A report author cannot sign off their own work', az: 'Hesabatın müəllifi öz işini təsdiqləyə bilməz' },
  comment: { ru: `Комментарий — не длиннее ${MAX_COMMENT} символов`, en: `Comment — ${MAX_COMMENT} characters max`, az: `Şərh ${MAX_COMMENT} simvoldan uzun olmamalıdır` },
} satisfies Record<string, Localized>

/** Текст 400 сервера → со всеми языками, если он нам известен; иначе как есть (русский). */
export function serverText(message: string): Localized {
  const known = Object.values(MSG).find((m) => m.ru === message)
  if (known) return known
  const dup = /^Модуль «(.+)» уже есть в заявке$/.exec(message)
  return dup ? dupModule(dup[1]) : message
}

export interface ItWorksFieldError { field: string; message: Localized }

/** Ошибки формы с путями полей сервера (`modules[1].approverId`) — порядок как у validateItWorks. */
export function itWorksErrors(f: ItWorksFormState, o: ItWorksOptions | undefined, meId?: string): ItWorksFieldError[] {
  const errors: ItWorksFieldError[] = []
  const err = (field: string, message: Localized) => errors.push({ field, message })

  // Шапка
  if (!f.contractorId || (o && !o.contractors.some((c) => c.id === f.contractorId))) err('contractorId', MSG.contractor)
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(f.period)) err('period', MSG.period)
  if (f.periodFrom && f.periodTo && f.periodTo < f.periodFrom) err('periodTo', MSG.periodTo)
  const amount = parseAmount(f.amount)
  if (amount === undefined || amount <= 0) err('amount', MSG.amount)
  if (!f.currency) err('currency', MSG.currency)

  // Отчёты и файлы
  if (f.direction === 'GEORGIA') {
    if (f.reports.filter((r) => r.kind === 'summary').length !== 1) err('reports', MSG.reportsGeorgia)
  } else if (!f.reports.some((r) => r.kind === 'report')) {
    err('reports', MSG.reportsBaku)
  }
  // Отчёт без файла сервер видит как файл неизвестного формата; клиент просит именно файл (путь тот же).
  f.reports.forEach((r, i) => { if (!r.file) err(`reports[${i}].uploadId`, MSG.reportFile) })
  const ids = [...f.reports.map((r) => r.file?.uploadId), f.invoice?.uploadId].filter((x): x is string => !!x)
  if (new Set(ids).size !== ids.length) err('files', MSG.dupFile)
  else if (ids.length > IT_WORKS_MAX_FILES) err('files', MSG.files)

  // Модули
  if (f.modules.length === 0) err('modules', MSG.noModules)
  const seenModules = new Set<string>()
  const seenOther = new Set<string>()
  f.modules.forEach((l, i) => {
    const at = `modules[${i}]`
    if (l.moduleId !== null) {
      const name = l.moduleId && (o ? o.modules.find((m) => m.id === l.moduleId)?.name : l.moduleId)
      if (!l.moduleId || name === undefined) err(`${at}.moduleId`, MSG.pickModule)
      else if (seenModules.has(l.moduleId)) err(`${at}.moduleId`, dupModule(name || l.moduleId))
      if (l.moduleId) seenModules.add(l.moduleId)
    } else {
      const name = l.otherName.trim()
      if (!name) err(`${at}.otherName`, MSG.otherName)
      else if (seenOther.has(name.toLowerCase())) err(`${at}.moduleId`, dupModule(name))
      seenOther.add(name.toLowerCase())
    }
    if (f.direction === 'BAKU' && !f.reports.some((r) => r.id === l.reportId)) err(`${at}.reportId`, MSG.lineReport)
    if (!l.approver) err(`${at}.approverId`, MSG.noApprover)
    else if (l.approver.id === meId) err(`${at}.approverId`, MSG.selfApprover)
  })

  // Финальный утверждающий
  const fa = f.finalApproverId
  if (!fa || (o && !o.finalApprovers.some((p) => p.id === fa))) err('finalApproverId', MSG.finalList)
  else if (fa === meId) err('finalApproverId', MSG.selfApprover)
  else if (f.modules.some((l) => l.approver?.id === fa)) err('finalApproverId', MSG.finalIsLineApprover)

  if (f.comment.length > MAX_COMMENT) err('comment', MSG.comment)
  return errors
}

/** Подписи того, что мешает отправке, — тексты сервера. */
export function itWorksMissing(f: ItWorksFormState, o: ItWorksOptions | undefined, meId?: string): Localized[] {
  return itWorksErrors(f, o, meId).map((e) => e.message)
}

/** «1 500,55» → 1500.55; пусто или мусор → undefined. */
export function parseAmount(s: string): number | undefined {
  const t = s.replace(',', '.').trim()
  if (!t) return undefined
  const n = Number(t)
  return Number.isFinite(n) ? n : undefined
}

const opt = (s: string) => s.trim() || undefined

/** Тело запроса: пустые строки не отправляем (DTO отбил бы '' у дат и периода). */
export function toItWorksInput(f: ItWorksFormState): ItWorksInput {
  return {
    direction: f.direction,
    contractorId: f.contractorId || undefined,
    period: f.period || undefined,
    periodFrom: f.periodFrom || undefined,
    periodTo: f.periodTo || undefined,
    amount: parseAmount(f.amount),
    currency: f.currency || undefined,
    invoiceUploadId: f.invoice?.uploadId,
    finalApproverId: f.finalApproverId || undefined,
    recipients: f.recipients.map((p) => p.id),
    comment: opt(f.comment),
    reports: f.reports.map((r) => ({
      id: r.id, kind: r.kind, uploadId: r.file?.uploadId, authorId: r.authorId || undefined, note: opt(r.note),
    })),
    modules: f.modules.map((l) => ({
      id: l.id,
      moduleId: l.moduleId,
      otherName: l.moduleId === null ? opt(l.otherName) : undefined,
      // Грузия связывает сводный со всеми строками, reportId там не нужен.
      reportId: f.direction === 'BAKU' ? l.reportId || undefined : undefined,
      approverId: l.approver?.id,
      note: opt(l.note),
    })),
  }
}

/**
 * id новых отчётов и строк выдаёт форма: строка Баку ссылается на отчёт по id ещё до сохранения,
 * а сервер присланный id сохраняет (`r.id || randomUUID()` в toPayload).
 */
export const newRowId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

/** Отчётный месяц по умолчанию — прошлый. */
export function defaultPeriod(now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Отчётный месяц `YYYY-MM` → «Сентябрь 2026»; не месяц — как есть. */
export function periodLabel(period: string, lang: Lang): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period)
  const name = m && MONTHS[lang][Number(m[2]) - 1]
  return m && name ? `${name} ${m[1]}` : period
}

/** Месяцы для выбора: текущий и `n - 1` прошлых, новые сверху. */
export function recentPeriods(now: Date, n = 24): string[] {
  return Array.from({ length: n }, (_, i) => defaultPeriod(new Date(now.getFullYear(), now.getMonth() - i + 1, 1)))
}

export function emptyItWorksForm(direction: ItWorksDirection, now: Date): ItWorksFormState {
  return {
    direction, contractorId: '', period: defaultPeriod(now), periodFrom: '', periodTo: '', amount: '', currency: '',
    invoice: null, finalApproverId: '', recipients: [], comment: '', reports: [], modules: [],
  }
}

/**
 * Умолчания направления: подрядчик, валюта (подрядчика, иначе направления), финальный утверждающий,
 * получатели — без инициатора, утверждающего и согласующих строк (они и так в маршруте).
 */
export function withDirectionDefaults(f: ItWorksFormState, o: ItWorksOptions, meId?: string): ItWorksFormState {
  const contractor = o.contractors.find((c) => c.id === o.defaultContractorId)
  const finalApproverId = o.defaultFinalApproverId ?? ''
  const route = new Set([meId, finalApproverId, ...f.modules.map((l) => l.approver?.id)])
  return {
    ...f,
    contractorId: contractor?.id ?? '',
    currency: contractorCurrency(contractor?.currency) ?? o.currency,
    finalApproverId,
    recipients: o.recipients.filter((p) => !route.has(p.id)),
  }
}

export const contractorCurrency = (c: string | undefined) =>
  IT_WORKS_CURRENCIES.find((x) => x === c)

/** Смена направления: отчёты другого вида и умолчания чужого направления сбрасываются. */
export function switchDirection(f: ItWorksFormState, direction: ItWorksDirection): ItWorksFormState {
  return {
    ...f, direction, contractorId: '', currency: '', finalApproverId: '', recipients: [], reports: [],
    modules: f.modules.map((l) => ({ ...l, reportId: '' })),
  }
}

/**
 * Можно ли добавить ещё отчёт или приложение: DTO принимает не больше 20 отчётов, а лимит заявки —
 * 20 файлов вместе со счётом. Строки отчётов без файла тоже в счёт: каждой нужен файл.
 */
export const canAddReport = (f: ItWorksFormState) => f.reports.length + (f.invoice ? 1 : 0) < IT_WORKS_MAX_FILES

/** Файлы, которые уйдут вместе с переотправкой доработки: загружены в этой правке и есть в форме. */
export function freshUploadIds(f: ItWorksFormState): string[] {
  return [...f.reports.map((r) => r.file), f.invoice].filter((x): x is ItWorksFile => !!x?.fresh).map((x) => x.uploadId)
}

/**
 * Проверка файла при выборе — раньше загрузки. Тип — по расширению: браузер не всегда знает MIME
 * у docx/xlsx. Отчёт: PDF/DOCX/XLSX, счёт: только PDF; ≤ 25 МБ.
 */
export function filePickError(file: { name: string; size: number }, kind: 'report' | 'invoice'): Localized | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (kind === 'invoice' ? ext !== 'pdf' : !['pdf', 'docx', 'xlsx'].includes(ext)) {
    return kind === 'invoice' ? MSG.invoice : MSG.reportFormat
  }
  if (file.size > IT_WORKS_MAX_FILE_BYTES) {
    return { ru: 'Файл больше 25 МБ', en: 'The file is larger than 25 MB', az: 'Fayl 25 MB-dan böyükdür' }
  }
  return null
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')
const arr = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter((x) => x && typeof x === 'object') : [])
const REPORT_KINDS: ItWorksReportKind[] = ['summary', 'attachment', 'report']

/**
 * Форма из payload заявки: открытый черновик (неполный — есть лишь `direction`) или доработка
 * (очищенные поля — `null`). Имена файлов — из вложений заявки: у черновика их нет (payload хранит
 * только uploadId), тогда файл без имени. Люди — из известных карточке (`people`); кого нет —
 * с пустым ФИО, форма дочитает его сама.
 */
export function fromItWorksPayload(
  raw: unknown,
  ctx: { attachments?: EdoAttachment[]; people?: (ItWorksPerson | null | undefined)[] } = {},
): ItWorksFormState {
  const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const fileOf = (uploadId: string): ItWorksFile | null =>
    uploadId ? { uploadId, name: ctx.attachments?.find((a) => a.uploadId === uploadId)?.upload.name } : null
  const person = (id: string): ItWorksPerson => ctx.people?.find((x) => x?.id === id) ?? { id, fullName: '' }
  const currency = contractorCurrency(str(p.currency))
  return {
    direction: p.direction === 'BAKU' ? 'BAKU' : 'GEORGIA',
    contractorId: str(p.contractorId),
    period: str(p.period),
    periodFrom: str(p.periodFrom),
    periodTo: str(p.periodTo),
    amount: typeof p.amount === 'number' ? String(p.amount) : '',
    currency: currency ?? '',
    invoice: fileOf(str(p.invoiceUploadId)),
    finalApproverId: str(p.finalApproverId),
    recipients: (Array.isArray(p.recipients) ? p.recipients : []).filter((x): x is string => typeof x === 'string').map(person),
    comment: str(p.comment),
    reports: arr(p.reports).map((r) => ({
      id: str(r.id) || newRowId(),
      kind: REPORT_KINDS.find((k) => k === r.kind) ?? (p.direction === 'BAKU' ? 'report' : 'attachment'),
      file: fileOf(str(r.uploadId)),
      authorId: str(r.authorId),
      note: str(r.note),
    })),
    modules: arr(p.modules).map((l) => ({
      id: str(l.id) || newRowId(),
      moduleId: l.moduleId === null ? null : str(l.moduleId) || undefined,
      otherName: str(l.otherName),
      reportId: str(l.reportId),
      approver: str(l.approverId) ? person(str(l.approverId)) : null,
      note: str(l.note),
    })),
  }
}
